import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { ChevronDown } from 'lucide-react';

const YouTubeChart = ({ data, loading = false, onYearChange }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Generate year options (current year back to 2020)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear; year >= 2020; year--) {
    yearOptions.push(year);
  }

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setIsDropdownOpen(false);
    if (onYearChange) {
      onYearChange(year);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.year-selector')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  if (loading) {
    return (
      <div className="dashboard-card">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-5 h-5 bg-dark-700 rounded animate-pulse"></div>
          <div className="h-6 bg-dark-700 rounded w-48 animate-pulse"></div>
        </div>
        <div className="h-80 bg-dark-700 rounded animate-pulse"></div>
      </div>
    );
  }

  // Transform API data to chart format  
  const chartData = data?.map(item => {
    const date = new Date(item.month + '-01');
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      fullMonth: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      views: item.total_views || 0,
      videos: item.total_videos || 0,
    };
  }) || [];

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toString();
  };

  // Custom label component for showing values on top of bars
  const CustomLabel = (props) => {
    const { x, y, width, value } = props;
    return (
      <text 
        x={x + width / 2} 
        y={y - 5} 
        fill="#f8fafc" 
        textAnchor="middle" 
        fontSize="12"
        fontWeight="500"
      >
        {formatYAxis(value)}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-dark-800 border border-dark-600 rounded-lg p-3 shadow-lg">
          <p className="text-white font-medium">{label}</p>
          <p className="text-purple-400">
            Views: <span className="font-semibold">{data.views.toLocaleString()}</span>
          </p>
          <p className="text-dark-300">
            Videos: <span className="font-semibold">{data.videos}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <div className="w-5 h-5 bg-gradient-to-br from-purple-500 to-purple-600 rounded"></div>
          <h3 className="text-xl font-semibold text-white">YouTube Views - {selectedYear === currentYear ? 'Past 12 Months' : selectedYear}</h3>
        </div>
        
        {/* Year Selector */}
        <div className="relative year-selector">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center space-x-2 px-4 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-white transition-colors"
          >
            <span>{selectedYear === currentYear ? 'Past 12 Months' : selectedYear}</span>
            <ChevronDown size={16} className={`transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-dark-700 rounded-lg shadow-lg border border-dark-600 z-10">
              <div className="py-2">
                <button
                  onClick={() => handleYearChange(currentYear)}
                  className={`w-full text-left px-4 py-2 hover:bg-dark-600 ${selectedYear === currentYear ? 'text-purple-400' : 'text-white'}`}
                >
                  Past 12 Months
                </button>
                {yearOptions.slice(1).map((year) => (
                  <button
                    key={year}
                    onClick={() => handleYearChange(year)}
                    className={`w-full text-left px-4 py-2 hover:bg-dark-600 ${selectedYear === year ? 'text-purple-400' : 'text-white'}`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis 
              dataKey={selectedYear === currentYear ? "fullMonth" : "month"}
              stroke="#64748b"
              fontSize={12}
              tickLine={false}
              angle={selectedYear === currentYear ? -45 : 0}
              textAnchor={selectedYear === currentYear ? "end" : "middle"}
              height={selectedYear === currentYear ? 60 : 40}
            />
            <YAxis 
              stroke="#64748b"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatYAxis}
            />
            <Bar 
              dataKey="views" 
              fill="url(#purpleGradient)"
              radius={[4, 4, 0, 0]}
            >
              <LabelList content={CustomLabel} />
            </Bar>
            <defs>
              <linearGradient id="purpleGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#6366f1" />
              </linearGradient>
            </defs>
            <CustomTooltip />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Optional: Show total stats below chart instead of redundant months */}
      <div className="mt-6 pt-4 border-t border-dark-700">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-dark-400 mb-1">Total Views</p>
            <p className="text-lg font-semibold text-white">
              {formatYAxis(chartData.reduce((sum, item) => sum + item.views, 0))}
            </p>
          </div>
          <div>
            <p className="text-xs text-dark-400 mb-1">Total Videos</p>
            <p className="text-lg font-semibold text-white">
              {chartData.reduce((sum, item) => sum + item.videos, 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-dark-400 mb-1">Avg Views/Video</p>
            <p className="text-lg font-semibold text-white">
              {chartData.length > 0 ? formatYAxis(Math.round(chartData.reduce((sum, item) => sum + item.views, 0) / chartData.reduce((sum, item) => sum + item.videos, 0))) : '0'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouTubeChart;